import { useEffect, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { StatCard } from '@/components/dashboard/StatCard'
import { Button } from '@/components/ui/button'
import {
  AppWindow,
  Activity,
  DoorOpen,
  Zap,
  ArrowUpRight,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface RecentRoom {
  id: string
  display_name: string
  status: string
  application_name: string
  created_at: string
  ended_at: string | null
}

interface SparkPoint {
  hour: string
  joined: number
}

interface Stats {
  total_applications: number
  active_rooms: number
  total_rooms: number
  total_events: number
  participants_24h: number
  participants_delta: number
  minutes_24h: number
  sparkline: SparkPoint[]
  generated_at: string
}

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  stats: Stats
  recent_rooms: RecentRoom[]
}

const POLL_MS = 5000

export default function Overview({
  user,
  organization,
  stats,
  recent_rooms,
}: Props) {
  const [pulsedAt, setPulsedAt] = useState(Date.now())

  // Live polling — Inertia partial reload, only refetches { stats, recent_rooms }
  useEffect(() => {
    const id = window.setInterval(() => {
      router.reload({
        only: ['stats', 'recent_rooms'],
        onSuccess: () => setPulsedAt(Date.now()),
      })
    }, POLL_MS)
    return () => window.clearInterval(id)
  }, [])

  const sparkSeries = stats.sparkline?.map((p) => p.joined) ?? []
  const peakHour = stats.sparkline?.reduce(
    (acc, p) => (p.joined > acc.joined ? p : acc),
    { hour: '—', joined: 0 },
  )

  return (
    <AppShell user={user} organization={organization}>
      <div className="space-y-8">
        {/* ───── Page header — editorial + live status ───── */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Overview · {new Date(stats.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </div>
            <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              {organization.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your organization at a glance. Refreshing every {POLL_MS / 1000}s.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <PulseBadge tickKey={pulsedAt} />
            <Button asChild>
              <Link href="/dashboard/applications/new">
                <Plus className="mr-1 h-4 w-4" />
                New application
              </Link>
            </Button>
          </div>
        </header>

        {/* ───── Empty state ───── */}
        {stats.total_applications === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* ───── Headline metric strip ───── */}
            <section className="surface-card signal-border relative overflow-hidden p-8 sm:p-10">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, hsl(var(--brand-2) / 0.22) 0%, transparent 70%)',
                }}
              />
              <div className="relative grid gap-8 lg:grid-cols-[1.2fr_2fr] lg:items-center">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Last 24 hours
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-heading text-7xl leading-none tracking-tight tabular-nums">
                      {stats.participants_24h.toLocaleString()}
                    </span>
                    <span className="font-mono text-sm text-muted-foreground">
                      participants joined
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
                    <span>≈ {stats.minutes_24h.toLocaleString()} minutes</span>
                    <span aria-hidden>·</span>
                    <span
                      className={cn(
                        stats.participants_delta >= 0 ? 'text-[hsl(var(--signal-live))]' : 'text-destructive',
                      )}
                    >
                      {stats.participants_delta >= 0 ? '↑' : '↓'} {Math.abs(stats.participants_delta).toFixed(1)}% vs prior 24h
                    </span>
                  </div>
                </div>

                {/* Hero sparkline */}
                <div className="relative h-36 w-full">
                  <HeroSparkline data={sparkSeries} />
                  <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">
                    <span>{stats.sparkline?.[0]?.hour ?? '00'}:00 utc</span>
                    <span>peak · {peakHour?.hour}:00 ({peakHour?.joined})</span>
                    <span>{stats.sparkline?.[stats.sparkline.length - 1]?.hour ?? '00'}:00 utc</span>
                  </div>
                </div>
              </div>
            </section>

            {/* ───── Stat cards ───── */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Active rooms"
                value={stats.active_rooms}
                icon={<Activity className="h-4 w-4" />}
                spark={sparkSeries}
                live
              />
              <StatCard
                title="Applications"
                value={stats.total_applications}
                icon={<AppWindow className="h-4 w-4" />}
              />
              <StatCard
                title="Rooms (lifetime)"
                value={stats.total_rooms}
                icon={<DoorOpen className="h-4 w-4" />}
              />
              <StatCard
                title="Events"
                value={stats.total_events}
                icon={<Zap className="h-4 w-4" />}
              />
            </section>

            {/* ───── Recent rooms ───── */}
            <section>
              <header className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Recent rooms
                  </div>
                  <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight">
                    Sessions
                  </h2>
                </div>
                <Link
                  href="/dashboard/applications"
                  className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                >
                  view all ↗
                </Link>
              </header>

              {recent_rooms.length === 0 ? (
                <div className="surface-card p-10 text-center">
                  <p className="text-sm text-muted-foreground">No rooms yet.</p>
                </div>
              ) : (
                <div className="surface-card divide-y divide-foreground/[0.04] overflow-hidden">
                  {recent_rooms.map((room) => (
                    <RoomRow key={room.id} room={room} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </AppShell>
  )
}

/* ──────────────────────────────────────────────────────────────── */

function PulseBadge({ tickKey }: { tickKey: number }) {
  // Re-render the spinner-pop on every successful poll
  return (
    <div
      key={tickKey}
      className="hidden items-center gap-2 rounded-md border border-foreground/10 bg-background/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:flex"
    >
      <RefreshCw className="h-3 w-3 animate-[reveal-up_0.5s_ease-out_both]" />
      <span className="live-dot" />
      live
    </div>
  )
}

function EmptyState() {
  return (
    <div className="surface-card signal-border relative overflow-hidden p-12 text-center">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top, hsl(var(--brand-2) / 0.18) 0%, transparent 60%)',
        }}
      />
      <div className="relative">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-foreground/[0.04]">
          <AppWindow className="h-6 w-6 text-foreground/70" strokeWidth={1.5} />
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Get started
        </div>
        <h2 className="mt-2 font-heading text-xl font-semibold tracking-tight sm:text-2xl">
          Create your first application
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
          Applications let you generate API keys and start making calls.
        </p>
        <Button asChild className="mt-6">
          <Link href="/dashboard/applications/new">
            <Plus className="mr-1 h-4 w-4" />
            Create application
          </Link>
        </Button>
      </div>
    </div>
  )
}

function RoomRow({ room }: { room: RecentRoom }) {
  const isActive = room.status === 'active'
  const created = new Date(room.created_at)
  return (
    <Link
      href="/dashboard/applications"
      className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-foreground/[0.025]"
    >
      <span
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          isActive
            ? 'bg-[hsl(var(--signal-live))] shadow-[0_0_0_4px_hsl(var(--signal-live)/0.18)]'
            : 'bg-muted-foreground/40',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-heading text-sm font-semibold">
          {room.display_name}
        </div>
        <div className="truncate font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {room.application_name} · {created.toLocaleString()}
        </div>
      </div>
      <span
        className={cn(
          'rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]',
          isActive
            ? 'bg-[hsl(var(--signal-live)/0.15)] text-[hsl(var(--signal-live))]'
            : 'bg-foreground/[0.06] text-muted-foreground',
        )}
      >
        {room.status}
      </span>
      <ArrowUpRight className="h-4 w-4 text-muted-foreground/50 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />
    </Link>
  )
}

function HeroSparkline({ data }: { data: number[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-full items-center justify-center font-mono text-xs text-muted-foreground/50">
        no traffic yet
      </div>
    )
  }
  const w = 600
  const h = 120
  const max = Math.max(1, ...data)
  const step = w / Math.max(1, data.length - 1)

  const points = data.map((v, i) => {
    const x = i * step
    const y = h - (v / max) * (h - 8) - 4
    return [x, y] as const
  })

  const path = points.reduce(
    (acc, [x, y], i) =>
      acc + (i === 0 ? `M${x.toFixed(1)} ${y.toFixed(1)}` : ` L${x.toFixed(1)} ${y.toFixed(1)}`),
    '',
  )
  const fillPath = `${path} L${w} ${h} L0 ${h} Z`

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="h-full w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id="hero-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--brand-2))" stopOpacity="0.35" />
          <stop offset="100%" stopColor="hsl(var(--brand-2))" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hero-spark-stroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(var(--brand-1))" />
          <stop offset="100%" stopColor="hsl(var(--brand-2))" />
        </linearGradient>
      </defs>
      {/* Reference grid */}
      {[0.25, 0.5, 0.75].map((t) => (
        <line
          key={t}
          x1="0"
          x2={w}
          y1={h * t}
          y2={h * t}
          stroke="hsl(var(--grid-line))"
          strokeOpacity="0.5"
          strokeDasharray="2 4"
        />
      ))}
      <path d={fillPath} fill="url(#hero-spark-fill)" />
      <path
        d={path}
        fill="none"
        stroke="url(#hero-spark-stroke)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.length > 0 && (
        <>
          <circle
            cx={points[points.length - 1][0]}
            cy={points[points.length - 1][1]}
            r="6"
            fill="hsl(var(--signal-live))"
            opacity="0.2"
          >
            <animate attributeName="r" values="4;9;4" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0;0.4" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle
            cx={points[points.length - 1][0]}
            cy={points[points.length - 1][1]}
            r="3.2"
            fill="hsl(var(--signal-live))"
          />
        </>
      )}
    </svg>
  )
}
